<template>
    <div class="flex">
        <div class="w-full">
          <div class="grid grid-cols-2 gap-3 py-3">
              <input type="text"
                     v-model="search"
                     placeholder="Search by Name"
                     required
                     class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                     @keyup.enter="fetch(1)"/>
              <input type="text"
                     v-model="email"
                     placeholder="Search by Email"
                     required
                     class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                     @keyup.enter="fetch(1)"/>
            </div>
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-12">
                    <table class="min-w-full leading-normal">
                        <thead>
                        <tr class="border-b border-t border-gray-200">
                          <th>Names</th>
                          <th>Emails</th>
                          <th>Phones</th>
                          <th>Date Created</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        <template v-if="loading">
                          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                            <td>
                              <div class="h-3 bg-gray-200 rounded w-full"></div>
                            </td>
                          </tr>
                        </template>
                        <template v-else>
                          <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(account, idx) in accounts" :key="idx">
                            <td class="font-bold text-uppercase">
                              <span>{{ account.individualName }}, {{ account.backOfficeName }}</span>
                            </td>
                            <td>
                              {{ account.individualEmail }}, {{ account.backOfficeEmail }}
                            </td>
                            <td>
                              {{ account.individualPhone }}, {{ account.backOfficePhone }}
                            </td>
                            <td>
                              <span>{{ formatDate(account.createdAt) }}</span>
                            </td>
                            <td>
                              <span class="text-green" v-if="account.status === 'ACTIVE'">Active</span>
                              <span class="text-red" v-else>Inactive</span>
                            </td>
                            <td>
                              <div class="flex gap-1 items-center">
                                <button
                                  @click="revokeReceiver(account.id)"
                                  class="btn-sm-danger">
                                  <i class="fa fa-times mr-1"></i> Revoke
                                </button>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td v-if="accounts.length === 0" colspan="6" class="text-center py-6 text-sm text-gray-400">
                              No results found
                            </td>
                          </tr>
                        </template>
                        </tbody>
                    </table>
                </div>
            </div>
          <div v-if="pagination.totalPages > 1"
               class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
            <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
              <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
            <div class="flex gap-2">
              <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
                <i class="fa fa-chevron-left mr-1"></i> Prev
              </button>
              <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
                Next <i class="fa fa-chevron-right ml-1"></i>
              </button>
            </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from "vue";
import { api } from "@/app/services/api.service.ts";
import { formatDate } from "@/commons/composables/utilities.ts";
import { useNotificationsStore } from "@/app/notify/notifications.ts";

interface Account {
    id: string
    individualName: string
    backOfficeName: string
    individualEmail: string
    backOfficeEmail: string
    individualPhone: string
    backOfficePhone: string
    createdAt: string
    status: string
}

interface Pagination {
    totalElements: number
    totalPages: number
}

const instance = getCurrentInstance()
const notify = useNotificationsStore()

const accounts = ref<Account[]>([])
const loading = ref(false)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref<Pagination>({
    totalElements: 0,
    totalPages: 0
})
const rows = ref({
    odd: "border-t border-gray-200 hover:bg-gray-10",
    even: "border-t border-gray-200 bg-gray-10"
})
const search = ref("")
const email = ref<string | null>(null)

onMounted(() => {
    fetch(1)
})

const fetch = (startPage: number | null = null) => {
    loading.value = true

    api().get("insolvency/official-receivers?", {
        params: {
            page: startPage ? startPage : currentPage.value,
            size: limit.value,
            search: search.value,
        }
    }).then(response => {
        loading.value = false
        accounts.value = response.data.content
        // pagination.value = response.data.pagination
    }).catch(error => {
        loading.value = false
        notify.error(error.message)
    })
}

const next = () => {
    if (currentPage.value < pagination.value.totalPages) {
        currentPage.value++
        fetch()
    }
}

const previous = () => {
    if (currentPage.value > 1) {
        currentPage.value--
        fetch()
    } else {
        notify.warning("No more data to show")
    }
}

const revokeReceiver = (id: string) => {
    api().post("insolvency/official-receivers/" + id + "/delete").then(() => {
        notify.success("Receiver has been revoked")
        fetch()
    }).catch(error => {
        notify.error(error.message)
    })
}
</script>
